import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Divider,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import {
    Person as PersonIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    DateRange as DateRangeIcon,
    AccessTime as AccessTimeIcon,
    AttachMoney as AttachMoneyIcon,
    Notes as NotesIcon,
    Repeat as RepeatIcon,
    LocationOn as LocationOnIcon,
    OpenInNew as OpenInNewIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { clientService } from '../../services/clientService';
import { staffService } from '../../services/staffService';

/**
 * Joins the four parts of a Client address into a single line:
 *   "123 Main St, Sydney NSW 2000"
 * Skips any field that's empty so we don't render stray commas.
 */
function composeAddress(
    street?: string | null,
    city?: string | null,
    state?: string | null,
    postcode?: string | null,
): string {
    const cityState = [city, state].filter(Boolean).join(' ');
    const cityStatePost = [cityState, postcode].filter(Boolean).join(' ');
    return [street, cityStatePost].filter(Boolean).join(', ');
}

interface Shift {
    id?: number;
    clientId: number;
    staffId: number;
    clientName?: string;
    staffName?: string;
    clientDetails?: {
        id: number;
        name: string;
        address: string;
    };
    staffDetails?: {
        id: number;
        name: string;
        employeeId: string;
        position: string;
    };
    shiftType: 'regular' | 'respite' | 'emergency' | 'overnight' | 'sleepover';
    startDateTime: string;
    endDateTime: string;
    durationHours?: number;
    status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
    actualStartTime?: string;
    actualEndTime?: string;
    careInstructions?: string;
    specialRequirements?: string;
    hourlyRate: number;
    totalAmount?: number;
    notes?: string;
    supervisorNotes?: string;
    /** Work information submitted by staff at clock-out or within 7 days post-clock-out. */
    workInformation?: string;
    /** UTC timestamp of last staff-initiated edit. */
    lastStaffEditAt?: string;
    createdById?: number;
    createdAt?: string;
    updatedAt?: string;
    isRecurrenceParent?: boolean;
    recurringUntil?: string;
    recurrencePattern?: string;
    clockInLatitude?: number | null;
    clockInLongitude?: number | null;
    clockInAccuracyMetres?: number | null;
    clockOutLatitude?: number | null;
    clockOutLongitude?: number | null;
    clockOutAccuracyMetres?: number | null;
    recurrenceId?: string;
    /** Travel kilometres recorded by staff at clock-out. */
    travelKilometres?: number | null;
    /** Inactive overnight (sleepover) shift flag. */
    isSleepover?: boolean;
    /** NDIS service category (PersonalCare/HomeHelp/CommunityAccess/Sleepover/Transport). */
    serviceCategory?: string;
    /** NDIS claim type. */
    claimType?: string;
    /** Cancellation charge percent (0/50/100) — only set when status === 'cancelled'. */
    cancellationChargePercent?: number | null;
    /** Whether the staff is paid for this cancelled shift. */
    payStaffForCancellation?: boolean | null;
}

interface ShiftDetailsModalProps {
    open: boolean;
    onClose: () => void;
    shift: Shift | null;
    /**
     * When true, the Financial Details card (hourly rate, total amount) is
     * hidden. Used on staff-facing pages where the displayed `hourlyRate` /
     * `totalAmount` are misleading because actual pay is computed from
     * Billing Rate segments + FlatRates, not from the values stored on the
     * shift.
     */
    hideFinancials?: boolean;
    /**
     * When provided, an "Edit Shift Info" button is shown on completed shifts
     * that are within the 7-day edit window. Calling this callback hands
     * control back to the parent to open the edit dialog.
     */
    onEditShiftInfo?: (shift: Shift) => void;
}

const ShiftDetailsModal: React.FC<ShiftDetailsModalProps> = ({ open, onClose, shift, hideFinancials = false, onEditShiftInfo }) => {
    // Fall-back lookup state — the Roster service doesn't populate
    // clientDetails/staffDetails on its responses, so when a caller hands us
    // a shift without those fields we lazily fetch them ourselves.
    const [fetchedClient, setFetchedClient] = useState<{ id: number; name: string; address: string } | null>(null);
    const [fetchedStaff, setFetchedStaff] = useState<{ id: number; name: string; employeeId: string; position: string } | null>(null);

    /** Returns true if the shift is clocked out and within the 7-day edit window. */
    const canEditShiftInfo = (s: Shift): boolean => {
        const endTime = s.actualEndTime;
        if (!endTime) return false;
        const endDate = new Date(endTime.endsWith('Z') ? endTime : endTime + 'Z');
        return endDate > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    };

    useEffect(() => {
        if (!open || !shift) {
            setFetchedClient(null);
            setFetchedStaff(null);
            return;
        }
        let cancelled = false;

        // Always fetch the full client when we have an id — even if the caller
        // pre-populated `clientDetails.address`, that value usually carries
        // only the street (the dropdown stores `client.address` alone, not
        // city/state/postcode). Fetching directly lets us compose the full
        // street + suburb + state + postcode line.
        if (shift.clientId) {
            clientService
                .getClient(shift.clientId)
                .then(c => {
                    if (cancelled) return;
                    const fullName = (c as any).fullName
                        ?? `${(c as any).firstName ?? ''} ${(c as any).lastName ?? ''}`.trim()
                        ?? '';
                    setFetchedClient({
                        id: c.id,
                        name: fullName || `Client ${c.id}`,
                        address: composeAddress(c.address, c.city, c.state, c.zipCode),
                    });
                })
                .catch(() => { /* silently skip — modal still renders */ });
        }

        if (shift.staffId) {
            staffService
                .getStaffProfile(shift.staffId)
                .then(s => {
                    if (cancelled) return;
                    setFetchedStaff({
                        id: s.id,
                        name: `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || `Staff ${s.id}`,
                        employeeId: s.employeeId ?? '',
                        position: s.position ?? '',
                    });
                })
                .catch(() => { /* silently skip */ });
        }

        return () => { cancelled = true; };
    }, [open, shift?.clientId, shift?.staffId, shift?.clientDetails?.address, shift?.staffDetails?.employeeId, shift?.staffDetails?.position]);

    if (!shift) return null;

    // Effective values: prefer freshly-fetched data when available (it has the
    // full composed address / employee id / position), fall back to whatever
    // the caller pre-populated.
    const effectiveClient = fetchedClient ?? shift.clientDetails ?? undefined;
    const effectiveStaff = fetchedStaff ?? shift.staffDetails ?? undefined;

    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
    
    const formatTime = (time: string) => {
        try {
            return format(new Date(time), 'h:mm a');
        } catch {
            return time;
        }
    };

    const formatDate = (date: string) => {
        try {
            return format(new Date(date), 'EEEE, MMMM d, yyyy');
        } catch {
            return date;
        }
    };

    const formatDateTime = (dateTime: string) => {
        try {
            return format(new Date(dateTime), 'MMM d, yyyy h:mm a');
        } catch {
            return dateTime;
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case 'scheduled':
                return 'primary';
            case 'in_progress':
                return 'info';
            case 'completed':
                return 'success';
            case 'cancelled':
                return 'error';
            case 'no_show':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getShiftTypeLabel = (type: string) => {
        switch (type) {
            case 'regular':
                return 'Regular';
            case 'respite':
                return 'Respite';
            case 'emergency':
                return 'Emergency';
            case 'overnight':
                return 'Overnight';
            default:
                return type;
        }
    };

    const calculateDuration = () => {
        if (shift.durationHours) {
            return shift.durationHours;
        }
        if (shift.startDateTime && shift.endDateTime) {
            const start = new Date(shift.startDateTime);
            const end = new Date(shift.endDateTime);
            return (end.getTime() - start.getTime()) / (1000 * 60 * 60); // Convert to hours
        }
        return 0;
    };

    const duration = calculateDuration();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: '400px'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" component="div" fontWeight="600">
                        Shift Details
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Chip 
                            label={getShiftTypeLabel(shift.shiftType)}
                            color="secondary"
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                        />
                        {shift.status && (
                            <Chip 
                                label={shift.status.replace('_', ' ')}
                                color={getStatusColor(shift.status)}
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                            />
                        )}
                    </Box>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Stack spacing={3}>
                    {/* Basic Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScheduleIcon fontSize="small" />
                                Schedule Information
                            </Typography>
                            
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                                <Box flex={1}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <DateRangeIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">Start:</Typography>
                                    </Stack>
                                    <Typography variant="body1" fontWeight="500">
                                        {formatDateTime(shift.startDateTime)}
                                    </Typography>
                                </Box>
                                
                                <Box flex={1}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <AccessTimeIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">End:</Typography>
                                    </Stack>
                                    <Typography variant="body1" fontWeight="500">
                                        {formatDateTime(shift.endDateTime)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Duration: {duration.toFixed(1)} hours
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {shift.status === 'cancelled' && (
                        <Card variant="outlined" sx={{ borderColor: 'warning.main', bgcolor: 'warning.lighter' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="warning.dark">
                                    Cancellation billing
                                </Typography>
                                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                                    <Box flex={1}>
                                        <Typography variant="body2" color="text.secondary">Client charge:</Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {shift.cancellationChargePercent === 0
                                                ? 'No charge'
                                                : shift.cancellationChargePercent === 50
                                                    ? '50% of scheduled shift'
                                                    : shift.cancellationChargePercent === 100
                                                        ? '100% of scheduled shift'
                                                        : 'Not set'}
                                        </Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography variant="body2" color="text.secondary">Pay staff?</Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {shift.payStaffForCancellation === true
                                                ? 'Yes — paid at the same percent as the client charge'
                                                : shift.payStaffForCancellation === false
                                                    ? 'No'
                                                    : 'Not set'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* People Information */}
                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                        <Card variant="outlined" sx={{ flex: 1 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon fontSize="small" />
                                    Staff Member
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {shift.staffName || effectiveStaff?.name || 'Not assigned'}
                                </Typography>
                                {effectiveStaff?.employeeId && (
                                    <Typography variant="body2" color="text.secondary">
                                        Employee ID: {effectiveStaff.employeeId}
                                    </Typography>
                                )}
                                {effectiveStaff?.position && (
                                    <Typography variant="body2" color="text.secondary">
                                        Position: {effectiveStaff.position}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card variant="outlined" sx={{ flex: 1 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon fontSize="small" />
                                    Client
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {shift.clientName || effectiveClient?.name || 'Not specified'}
                                </Typography>
                                {effectiveClient?.address && (
                                    <Typography variant="body2" color="text.secondary">
                                        Address: {effectiveClient.address}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Financial Information */}
                    {/* {!hideFinancials && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AttachMoneyIcon fontSize="small" />
                                    Financial Details
                                </Typography>

                                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                                    <Box flex={1}>
                                        <Typography variant="body2" color="text.secondary">Hourly Rate:</Typography>
                                        <Typography variant="h6" color="success.main">
                                            {formatCurrency(shift.hourlyRate)}
                                        </Typography>
                                    </Box>

                                    <Box flex={1}>
                                        <Typography variant="body2" color="text.secondary">Duration:</Typography>
                                        <Typography variant="h6">
                                            {duration.toFixed(1)} hours
                                        </Typography>
                                    </Box>

                                    <Box flex={1}>
                                        <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                                        <Typography variant="h6" color="success.main" fontWeight="600">
                                            {formatCurrency(shift.totalAmount || (shift.hourlyRate * duration))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )} */}

                    {/* Time Tracking */}
                    {(shift.actualStartTime || shift.actualEndTime) && (
                        <Card variant="outlined" sx={{ backgroundColor: 'info.main', color: 'info.contrastText' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon fontSize="small" />
                                    Actual Time Tracking
                                </Typography>
                                
                                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                                    {shift.actualStartTime && (
                                        <Box flex={1}>
                                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Actual Start:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {formatDateTime(shift.actualStartTime)}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {shift.actualEndTime && (
                                        <Box flex={1}>
                                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Actual End:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {formatDateTime(shift.actualEndTime)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Recurring Information */}
                    {shift.isRecurrenceParent && (
                        <Card variant="outlined" sx={{ backgroundColor: 'action.hover' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <RepeatIcon fontSize="small" />
                                    Recurring Shift
                                </Typography>
                                
                                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                                    {shift.recurrencePattern && (
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary">Pattern:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {shift.recurrencePattern}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {shift.recurringUntil && (
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary">Ends:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {formatDate(shift.recurringUntil)}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {shift.recurrenceId && (
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary">Recurrence ID:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {shift.recurrenceId}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Clock-in / Clock-out GPS Location */}
                    {(
                        (shift.clockInLatitude != null && shift.clockInLongitude != null) ||
                        (shift.clockOutLatitude != null && shift.clockOutLongitude != null)
                    ) && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOnIcon fontSize="small" />
                                    Staff Location at Clock In / Out
                                </Typography>

                                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                                    {shift.clockInLatitude != null && shift.clockInLongitude != null && (
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary">Clock-in:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {Number(shift.clockInLatitude).toFixed(6)}, {Number(shift.clockInLongitude).toFixed(6)}
                                                {shift.clockInAccuracyMetres != null && (
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        {' '}(±{Number(shift.clockInAccuracyMetres).toFixed(0)}m)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="text"
                                                startIcon={<OpenInNewIcon fontSize="small" />}
                                                href={`https://www.google.com/maps/search/?api=1&query=${shift.clockInLatitude},${shift.clockInLongitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ mt: 0.5, textTransform: 'none', px: 0 }}
                                            >
                                                View on map
                                            </Button>
                                        </Box>
                                    )}

                                    {shift.clockOutLatitude != null && shift.clockOutLongitude != null && (
                                        <Box flex={1}>
                                            <Typography variant="body2" color="text.secondary">Clock-out:</Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {Number(shift.clockOutLatitude).toFixed(6)}, {Number(shift.clockOutLongitude).toFixed(6)}
                                                {shift.clockOutAccuracyMetres != null && (
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        {' '}(±{Number(shift.clockOutAccuracyMetres).toFixed(0)}m)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="text"
                                                startIcon={<OpenInNewIcon fontSize="small" />}
                                                href={`https://www.google.com/maps/search/?api=1&query=${shift.clockOutLatitude},${shift.clockOutLongitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ mt: 0.5, textTransform: 'none', px: 0 }}
                                            >
                                                View on map
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                                {effectiveClient?.address && (
                                    <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Compare against client address: <strong>{effectiveClient.address}</strong>
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<OpenInNewIcon fontSize="small" />}
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(effectiveClient.address)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ mt: 0.5, textTransform: 'none', px: 0 }}
                                        >
                                            Open client address on map
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Instructions and Notes */}
                    {(shift.careInstructions || shift.specialRequirements || shift.notes || shift.supervisorNotes || shift.workInformation || (typeof shift.travelKilometres === 'number' && shift.travelKilometres > 0) || (onEditShiftInfo && canEditShiftInfo(shift))) && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NotesIcon fontSize="small" />
                                    Instructions & Notes
                                </Typography>
                                
                                <Stack spacing={2}>
                                    {shift.notes && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                General Notes:
                                            </Typography>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {shift.notes}
                                            </Typography>
                                        </Box>
                                    )}

                                    {shift.workInformation && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Work Information:
                                            </Typography>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {shift.workInformation}
                                            </Typography>
                                            {shift.lastStaffEditAt && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Last updated by staff: {new Date(shift.lastStaffEditAt.endsWith('Z') ? shift.lastStaffEditAt : shift.lastStaffEditAt + 'Z').toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    {/* Travel kilometres — recorded by staff at
                                        clock-out. Reimbursed to staff and
                                        on-charged to the participant per the
                                        FlatRates per-km rate. */}
                                    {typeof shift.travelKilometres === 'number' && shift.travelKilometres > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Travel Kilometres:
                                            </Typography>
                                            <Typography variant="body1">
                                                {shift.travelKilometres.toFixed(2)} km
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Edit Shift Info button — only shown when the parent
                                        passes onEditShiftInfo and the shift is editable. */}
                                    {onEditShiftInfo && canEditShiftInfo(shift) && (
                                        <Box>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() => { onClose(); onEditShiftInfo(shift); }}
                                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                            >
                                                Edit Work Information & Travel KM
                                            </Button>
                                        </Box>
                                    )}
                                    
                                    {shift.supervisorNotes && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Supervisor Notes:
                                            </Typography>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {shift.supervisorNotes}
                                            </Typography>
                                        </Box>
                                    )}
                                    {shift.careInstructions && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Care Instructions:
                                            </Typography>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {shift.careInstructions}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {shift.specialRequirements && (
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Special Requirements:
                                            </Typography>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {shift.specialRequirements}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* System Information */}
                    <Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary" align="center">
                            Shift ID: {shift.id}
                            {shift.createdAt && (
                                <> • Created: {formatDateTime(shift.createdAt)}</>
                            )}
                            {shift.updatedAt && (
                                <> • Last Updated: {formatDateTime(shift.updatedAt)}</>
                            )}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="outlined" size="large">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShiftDetailsModal;